import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { Coins } from 'lucide-react';
import { toast } from 'react-toastify';
import { SectionCard } from './resaleDetails';
import { NumberInput } from './resaleSettings';
import { Button as CustomButton } from '../../components/Button';
import { useTranslation } from '../../hooks/useTranslation';
import api from '../../services/api';

const inputSx = {
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#E0E0E0' },
    '&:hover fieldset': { borderColor: '#83D84D' },
    '&.Mui-focused fieldset': { borderColor: '#83D84D' },
  },
};

const computeUnitPrice = (amount, price) => {
  const amt = parseFloat(amount);
  const prc = parseFloat(price);
  if (amt > 0 && prc > 0) return (prc / amt).toFixed(2);
  return '';
};

const ConsultationValuesSection = ({ resaleId }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasCustom, setHasCustom] = useState(false);
  const [expired, setExpired] = useState(false);

  const [unitPrice, setUnitPrice] = useState('');
  const [packages, setPackages] = useState([]);
  const [validUntil, setValidUntil] = useState('');

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/resale/${resaleId}/consultation-config`);

      const usingCustom = data.has_custom;
      setHasCustom(usingCustom);
      setExpired(data.expired);

      const baseUnit = usingCustom && data.unit_price != null
        ? data.unit_price
        : data.defaults?.unit_price;
      setUnitPrice(baseUnit != null ? String(baseUnit) : '');

      const basePackages = usingCustom && data.packages?.length
        ? data.packages
        : data.defaults?.packages ?? [];
      setPackages(
        basePackages.map((p) => ({
          amount: String(p.amount),
          price: String(p.price),
          unit_price: String(p.unit_price),
        }))
      );

      setValidUntil(usingCustom && data.valid_until ? data.valid_until : '');
    } catch {
      toast.error(t('resale.consultValues.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [resaleId, t]);

  useEffect(() => {
    if (resaleId) fetchConfig();
  }, [resaleId, fetchConfig]);

  const handlePackageChange = (index, field, value) => {
    setPackages((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      updated[index].unit_price = computeUnitPrice(
        updated[index].amount,
        updated[index].price
      );
      return updated;
    });
  };

  const addPackage = () => {
    setPackages((prev) => [...prev, { amount: '', price: '', unit_price: '' }]);
  };

  const removePackage = (index) => {
    setPackages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        unit_price: unitPrice === '' ? null : parseFloat(unitPrice),
        packages: packages
          .filter((p) => p.amount && p.price)
          .map((p) => ({
            amount: parseInt(p.amount, 10),
            price: parseFloat(p.price),
            unit_price: parseFloat(p.unit_price) || 0,
          })),
        valid_until: validUntil || null,
      };

      await api.put(`/v1/resale/${resaleId}/consultation-config`, body);
      toast.success(t('resale.consultValues.savedSuccess'));
      await fetchConfig();
    } catch {
      toast.error(t('resale.consultValues.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const statusChip = () => {
    if (expired) {
      return (
        <Chip
          size="small"
          label={t('resale.consultValues.expiredBadge')}
          sx={{ backgroundColor: '#FDECEA', color: '#C62828', fontSize: '11px', fontWeight: 600 }}
        />
      );
    }
    if (hasCustom) {
      return (
        <Chip
          size="small"
          label={t('resale.consultValues.customBadge')}
          sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontSize: '11px', fontWeight: 600 }}
        />
      );
    }
    return (
      <Chip
        size="small"
        label={t('resale.consultValues.defaultBadge')}
        sx={{ backgroundColor: '#F5F5F5', color: '#757575', fontSize: '11px', fontWeight: 600 }}
      />
    );
  };

  return (
    <SectionCard
      icon={<Coins size={18} color="#757575" strokeWidth={1.5} />}
      title={t('resale.consultValues.title')}
      subtitle={t('resale.consultValues.subtitle')}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>{statusChip()}</Box>

          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1D2530', mb: '6px' }}>
            {t('resale.consultValues.unitLabel')}
          </Typography>
          <TextField
            value={unitPrice}
            onChange={(e) => setUnitPrice(e.target.value)}
            type="number"
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ color: '#9E9E9E', fontSize: '14px' }}>R$</Typography>
                </InputAdornment>
              ),
            }}
            sx={{ ...inputSx, mb: '6px', width: '220px' }}
          />
          <Typography sx={{ fontSize: '12px', color: '#9E9E9E', mb: '24px' }}>
            {t('resale.consultValues.unitHelper')}
          </Typography>

          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1D2530', mb: '4px' }}>
            {t('resale.consultValues.packagesTitle')}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#9E9E9E', mb: '16px' }}>
            {t('resale.consultValues.packagesSubtitle')}
          </Typography>

          <Box sx={{ overflowX: 'auto', mb: '16px' }}>
            {packages.map((pkg, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start',
                  mb: '16px',
                  minWidth: '500px',
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: '12px', color: '#737373', mb: '6px' }}>
                    {t('resale.consultValues.colAmount')}
                  </Typography>
                  <NumberInput
                    value={Number(pkg.amount) || 0}
                    onChange={(val) => handlePackageChange(index, 'amount', String(val))}
                    min={1}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '12px', color: '#737373', mb: '6px' }}>
                    {t('resale.consultValues.colTotal')}
                  </Typography>
                  <TextField
                    value={pkg.price}
                    onChange={(e) => handlePackageChange(index, 'price', e.target.value)}
                    type="number"
                    inputProps={{ min: 0, step: '0.01' }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ color: '#9E9E9E', fontSize: '14px' }}>R$</Typography>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    sx={inputSx}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '12px', color: '#737373', mb: '6px' }}>
                    {t('resale.consultValues.colUnit')}
                  </Typography>
                  <TextField
                    value={pkg.unit_price}
                    disabled
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ color: '#9E9E9E', fontSize: '14px' }}>R$</Typography>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    sx={{ ...inputSx, backgroundColor: '#F5F5F5' }}
                  />
                </Box>
                <IconButton
                  onClick={() => removePackage(index)}
                  sx={{ color: '#E53935', mt: '24px', flexShrink: 0 }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            ))}
          </Box>

          <Box
            onClick={addPackage}
            sx={{
              border: '2px dashed #E0E0E0',
              borderRadius: '8px',
              py: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer',
              color: '#737373',
              mb: '24px',
              maxWidth: '520px',
              '&:hover': { borderColor: '#83D84D', color: '#83D84D' },
            }}
          >
            <AddIcon fontSize="small" />
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
              {t('resale.consultValues.addPackage')}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1D2530', mb: '6px' }}>
            {t('resale.consultValues.validUntilLabel')}
          </Typography>
          <TextField
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            type="date"
            InputLabelProps={{ shrink: true }}
            sx={{ ...inputSx, width: '220px', mb: '6px' }}
          />
          <Typography sx={{ fontSize: '12px', color: '#9E9E9E', mb: '8px' }}>
            {t('resale.consultValues.validUntilHelper')}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <CustomButton fullWidth={false} onClick={handleSave} disabled={saving}>
              {saving ? (
                <CircularProgress size={20} />
              ) : (
                <Typography sx={{ fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                  <CheckIcon sx={{ mr: 1, fontSize: 18 }} />
                  {t('resale.consultValues.save')}
                </Typography>
              )}
            </CustomButton>
          </Box>
        </>
      )}
    </SectionCard>
  );
};

export default ConsultationValuesSection;
